<template>
  <div class="flex flex-col px-4 py-6 mx-auto my-0 max-w-screen-2xl md:py-5 md:px-10 lg:px-20">
    <div v-if="sectionTitle" class="flex flex-row mb-2">
      <ContentfulImage v-if="icon" class="w-6 h-6" :entry="icon" />
      <h3 class="ml-2 font-bold text-primary-900">{{ sectionTitle }}</h3>
    </div>
    <div class="grid grid-cols-1 gap-3 mb-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xl:gap-4">
      <CategoryCard v-for="(category, index) in displayedCategories" :key="index" :content="category" />
    </div>
    <div v-if="totalCategories > visibleTotal" class="flex flex-col items-center flex-center">
      <span class="mb-2 text-sm text-gray-800">
        {{
          t('displayed_category_count', {
            total: totalCategories,
            count: visibleCategoriesCount,
          })
        }}
      </span>
      <CButton variant="secondary" size="medium" @click="toggleExpanded()">
        {{ isExpanded ? t('show_less_categories') : t('show_all_categories') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

type Props = {
  categories?: any[];
  sectionTitle?: string;
  sectionIcon: any;
};

const props = withDefaults(defineProps<Props>(), {
  categories: () => [],
  sectionTitle: '',
  sectionIcon: undefined,
});

const { t } = useI18n();

const isExpanded = ref(false);
const visibleTotal = ref(12);

const displayedCategories = computed(() => {
  const categoriesList = props.categories || [];
  return isExpanded.value ? categoriesList : categoriesList.slice(0, visibleTotal.value);
});

const visibleCategoriesCount = computed(() => {
  return isExpanded.value ? props.categories.length : visibleTotal.value;
});

const icon = computed(() => {
  return props.sectionIcon;
});

const totalCategories = computed(() => {
  return props.categories?.length;
});

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const handleResize = () => {
  visibleTotal.value = window.innerWidth < 768 ? 6 : 12;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'CategorySection',
});
</script>
